<template>
    <div class="card">
        <div class="card-header">
            <h5 class="mb-0 d-inline">
                {{ name }}
            </h5>
            <button v-on:click="stop"
                v-if="running"
                class="btn btn-danger collapsed float-right"
                style="margin: 0 .5rem;"
                aria-expanded="false" aria-controls="collapseTwo">
                Stop
            </button>
            <button v-on:click="run"
                :disabled="running"
                class="btn btn-success collapsed float-right"
                style="margin: 0 .5rem;"
                aria-expanded="false" aria-controls="collapseTwo">
                {{ running ? 'Running...' : 'Run' }}
            </button>
            <button v-if="hasArguments && !hideArguments"
                    v-on:click="toggle"
                    class="btn btn-secondary collapsed float-right"
                    style="margin: 0 .5rem;"
                    aria-expanded="false" aria-controls="collapseTwo">
                {{ showing ? 'Hide' : 'Show' }}
            </button>
            <div v-if="progress" v-for="step in progressSteps">
                <p class="mt-2 d-block">
                    <small v-html="
                    '<b>Step %t:</b> %s (%d%)'
                    .replace('%t', step.index)
                    .replace('%s', step.message || 'Done!')
                    .replace('%d', step.percentage)
                    "></small>
                </p>
                <div v-if="progress" class="progress mb-3">
                    <span :style="'width: ' + step.percentage + '%;'"
                        :class="[
                            'progress-bar', 'd-block',
                            step.status == 'finished' ? 'success' : '',
                            step.status == 'stopped' ? 'warning' : '',
                        ]"></span>
                </div>
            </div>
        </div>

        <div v-if="!hideArguments" :class="['collapse', showing ? 'show' : '']"
            aria-labelledby="headingOne">
            <div class="card-body">
                <!-- arguments list -->
                <template v-for="entry in args">
                    <b-form-group
                        v-if="entry.type == 'string'"
                        :key="entry.name"
                        :label="__(entry.name, ['titleCase']) + ':'"
                        :description="entry.description || ''"
                        :label-for="entry.name"
                        horizontal>
                        <b-input-group>
                            <b-form-input
                                type="text"
                                :value="_.get(settings, entry.name, entry.default)"
                                v-on:input="(value) => update(entry.name, value)"
                                autocomplete="off"
                                :required="entry.required">
                            </b-form-input>
                        </b-input-group>
                    </b-form-group>
                    <b-form-group
                        v-if="entry.type == 'array'"
                        :key="entry.name"
                        :label="__(entry.name, ['titleCase']) + ':'"
                        :description="entry.description || ''"
                        :label-for="entry.name"
                        horizontal>
                        <b-input-group>
                            <b-form-input
                                type="text"
                                :value="_.get(settings, entry.name, entry.default)"
                                v-on:input="(value) => update(entry.name, value)"
                                autocomplete="off"
                                :required="entry.required">
                            </b-form-input>
                        </b-input-group>
                    </b-form-group>
                    <b-form-group
                        v-if="entry.type == 'boolean'"
                        :key="entry.name"
                        :label="__(entry.name, ['titleCase']) + ':'"
                        :description="entry.description || ''"
                        :label-for="entry.name"
                        horizontal>
                        <b-input-group>
                            <switches
                                v-on:input="(value) => update(entry.name, value)"
                                :value="getArgumentValue(entry)"
                                theme="bootstrap" :color="getArgumentValue(entry) ? 'primary' : 'default'"
                                ></switches>
                        </b-input-group>
                    </b-form-group>
                </template>

                <template v-for="entry in opts">
                    <b-form-group
                        v-if="entry.type == 'string'"
                        :key="entry.name"
                        :label="__(entry.name, ['titleCase']) + '(optional):'"
                        :description="entry.description || ''"
                        :label-for="entry.name"
                        horizontal>
                        <b-input-group>
                            <b-form-input
                                type="text"
                                :value="_.get(settings, entry.name, entry.default)"
                                v-on:input="(value) => update('--' + entry.name, value)"
                                autocomplete="off"
                                :required="entry.required">
                            </b-form-input>
                        </b-input-group>
                    </b-form-group>
                    <b-form-group
                        v-if="entry.type == 'array'"
                        :key="entry.name"
                        :label="__(entry.name, ['titleCase']) + '(optional):'"
                        :description="entry.description || ''"
                        :label-for="entry.name"
                        horizontal>
                        <b-input-group>
                            <b-form-input
                                type="text"
                                :value="_.get(settings, entry.name, entry.default)"
                                v-on:input="(value) => update('--' + entry.name, value)"
                                autocomplete="off"
                                :required="entry.required">
                            </b-form-input>
                        </b-input-group>
                    </b-form-group>
                    <b-form-group
                        v-if="entry.type == 'boolean'"
                        :key="entry.name"
                        :label="__(entry.name, ['titleCase']) + ' (optional):'"
                        :description="entry.description || ''"
                        :label-for="entry.name"
                        horizontal>
                        <b-input-group>
                            <switches
                                v-on:input="(value) => update('--' + entry.name, value)"
                                :value="getOptionValue(entry)"
                                theme="bootstrap" :color="getOptionValue(entry) ? 'primary' : 'default'"
                                ></switches>
                        </b-input-group>
                    </b-form-group>
                </template>
            </div>
        </div>
    </div>
</template>
<script>
import Switches from 'vue-switches';

export default {
    data() {
        return {};
    },

    props: [
        'name', 'schema',
        'progress', 'settings',
        'showing', 'running', 'tracking',
        'hideArguments',
    ],

    components: {
        Switches,
    },

    watch: {
        showing() {
            this.$forceUpdate();
        },

        running: {
            handler(newVal) {
                if (newVal && this.tracking) {
                    this.$emit('track');
                }
            },
            immediate: true,
        }
    },

    computed: {
        opts() {
            return _.get(this.schema, 'options', []);
        },

        args() {
            return _.get(this.schema, 'arguments', []);
        },

        hasArguments() {
            return !_.isEmpty(this.args) || !_.isEmpty(this.opts);
        },

        progressSteps() {
            let index = 1;
            const steps = _.chain(this.progress)
                .get('step', 1)
                .times(_.constant({
                    index: (index = index++),
                    status: 'success',
                    percentage: 100,
                }))
                .value();

            _.set(steps, '0', {
                index: steps.length,
                percentage: _.get(this.progress, 'percentage', 0),
                message: _.get(this.progress, 'message', 'Processing'),
                status: _.get(this.progress, 'status', 'processing'),
            })

            return steps;
        }
    },

    methods: {
        run() {
            this.$emit('run');
        },

        stop() {
            this.$emit('stop');
        },

        toggle() {
            this.$emit(this.showing ? 'hide' : 'show');
        },

        update(field, value) {
            this.$emit('input', { ...this.settings, [field]: value });
        },

        getArgumentValue(entry) {
            return _.get(this.settings, entry.name, entry.default);
        },

        getOptionValue(entry) {
            return _.get(this.settings, `--${entry.name}`, entry.default);
        },
    }
}
</script>



// WEBPACK FOOTER //
// seventytwo/72cms-ui/src/components/dashboard-elements/CommandComponent.vue